/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Button from '@material-ui/core/Button';
import withStyles from '@material-ui/core/styles/withStyles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { withRouter } from 'react-router';
import {
    object, string, func, shape, array, bool, number, arrayOf,
} from 'prop-types';

// gql & helper functions
import { ducks as checkoutDucks } from '../../../../../../../state/ducks/Checkout';
import { getContactId } from '../../../../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { getShopTheSite } from '../../../../../../pages/Account/utils/shopTheSite';
import { isEmpty } from '../../../../../../pages/Account/utils/object';
import { getIsProductFilterZipcodeValid, getWineData } from '../../../../../../../state/ducks/App/App-Selectors';
import { getFeatureFlags } from '../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';

const {
    common: {
        commonSelectors: {
            getLoaderStatus,
            getError,
        },
    },
    checkout: { checkoutActions },
} = checkoutDucks;

const styles = ({ palette, typography, breakpoints }) => ({
    buttonWrap: {
        width: '100%',
        boxSizing: 'border-box',
        marginTop: '15px',
    },
    addToCartButton: {
        color: palette.white,
        fontFamily: typography.fontFamily,
        boxSizing: 'border-box',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        fontWeight: '700',
        width: '235px',
        height: '50px',
        fontSize: '1em',
        textTransform: 'uppercase',
        borderRadius: '28px',
        lineHeight: '1.46em',
        padding: '7px 24px 5px 24px',
        '&:disabled': {
            backgroundColor: palette.cms?.wineVerifyDisable,
            color: palette.disableButton,
        },
    },
    addSubscriptionToCartButton: {
        color: palette.white,
        fontFamily: typography.fontFamily,
        boxSizing: 'border-box',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        fontWeight: '600',
        width: '255px',
        height: '50px',
        fontSize: '1em',
        textTransform: 'uppercase',
        borderRadius: '28px',
        lineHeight: '1.46em',
        '&:disabled': {
            backgroundColor: palette.cms?.wineVerifyDisable,
            color: palette.disableButton,
        },
        // Mobile
        [breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    cyoAddToCartButton: {
        color: palette.white,
        fontFamily: typography.fontFamily,
        boxSizing: 'border-box',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        fontWeight: '700',
        width: '100%',
        height: '40px',
        fontSize: '1em',
        textTransform: 'uppercase',
        borderRadius: '4px',
        lineHeight: '1.46em',
        padding: '5px 24px 5px 24px',
        [breakpoints.down(959)]: {
            marginRight: '10px',
            fontSize: '14px !important',
            width: '-webkit-fill-available',
        },
    },
    addToCartBg: {
        backgroundColor: palette.primaryButton,
    },
    addToCartButtonFullWidth: {
        color: palette.white,
        backgroundColor: palette.primaryButton,
        fontFamily: typography.fontFamily,
        boxSizing: 'border-box',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        fontWeight: '700',
        width: '100%',
        height: '50px',
        fontSize: '1em',
        textTransform: 'uppercase',
        borderRadius: '28px',
        lineHeight: '1.46em',
        padding: '7px 24px 5px 24px',
        '&:disabled': {
            backgroundColor: palette.cms?.wineVerifyDisable,
            color: palette.primary?.contrastText,
        },
    },
    sorryMessage: {
        fontSize: '15px',
        color: palette.cms?.errorZipVerifyPDP,
        fontFamily: typography?.fontFamily,
        textAlign: 'center',
        margin: '12px 0px 0px 0px',
    },
    outOfStockMessage: {
        fontSize: '15px',
        color: palette.cms?.errorZipVerifyPDP,
        fontFamily: typography?.fontFamily,
        textAlign: 'center',
        margin: 0,
    },
});

const DesktopFoodAddToCart = ({
    classes,
    history,
    selectedItemId,
    brandId,
    actionAddToCart,
    partNumber,
    categoryId,
    subscription,
    subscriptionDetails,
    productType,
    totalProductSelected,
    maxComponents,
    fullWidth,
    cyoSelectedComponents,
    disableAddToCart,
    personalizedData,
    isPersonalizable,
    isAddToCartEnabled,
    wineValidation,
    categoryPath,
    categoryName,
    addons,
    closeAddonModal,
    handleMiniCartModalClick,
    contactId,
    quantity,
    error,
    featureFlags,
    isBandItemSelected,
    handleMobileAgeVerification,
    isPassportBundleProduct,
    hasWine,
    setAddToCartClicked,
    selectedState,
}) => {
    const MINI_CART_ENABLED = featureFlags['is-mini-cart-enabled'];
    const enableMinicart = (MINI_CART_ENABLED && useMediaQuery((theme) => theme?.breakpoints.up('md')));
    const handleAddToCart = (productId = null, returnObject = false) => {
        setAddToCartClicked(true);
        if (!selectedState) return null;
        const reqObj = {
            brandCode: brandId,
            productCode: (productId || selectedItemId),
            quantity: Math.floor(Number(quantity)) || 1,
            attributes: [], // this will be used to track Gift List product add to cart
        };

        if (productType === 'CLUB' || productType === 'COLLECTION') {
            reqObj.lineItemType  = productType;
        }

        if (subscription?.subscriptionCheckbox
            && subscriptionDetails
            && subscriptionDetails.subscriptionDurationKey
            && subscriptionDetails.subscriptionFrequency
            && subscriptionDetails.subscriptionType) {
            let duration = subscriptionDetails.subscriptionDurationKey;
            if (duration.indexOf('.') > 0) {
                duration = duration.substring(0, duration.indexOf('.'));
            }

            let interval = subscriptionDetails.subscriptionFrequency;
            if (interval.indexOf('.') > 0) {
                interval = interval.substring(0, interval.indexOf('.'));
            }

            reqObj.subscription = {
                duration,
                interval,
                type: subscriptionDetails.subscriptionType,
            };
        }
        // GiftList - get Shop The Site content
        const giftList = getShopTheSite(contactId) || {};
        if (!isEmpty(giftList) && giftList.products) {
            // Get xOrderAttrValues from the first product for 4.2
            const [{ xOrderAttrValues = {} }] = giftList.products;
            delete giftList.products;
            reqObj.giftList = { ...giftList, xOrderAttrValues, contactId };
        }
        if (productType === 'cyo') {
            reqObj.components = cyoSelectedComponents;
        }

        if (hasWine && wineValidation?.wineVerify) {
            reqObj.wineVerify = wineValidation?.wineVerify;
        }

        if (isPersonalizable && personalizedData?.personalizationType) {
            reqObj.personalization = personalizedData;
        }

        if (addons.length > 0) {
            if (cyoSelectedComponents.length > 0) {
                reqObj.components = [...cyoSelectedComponents, ...addons];
            } else {
                reqObj.components = addons;
            }
        }
        let passportBundle = {};
        if (isPassportBundleProduct?.enable) {
            passportBundle = {
                enable: true,
                passportSku: isPassportBundleProduct?.passportSku,
            };
        }

        if (returnObject) {
            return {
                reqObj,
                history,
                partNumber,
                categoryId,
                categoryName,
                enableMinicart,
                categoryPath,
                handleMiniCartModalClick,
            };
        }
        actionAddToCart(
            reqObj,
            history,
            partNumber,
            categoryId,
            categoryName,
            categoryPath,
            enableMinicart,
            handleMiniCartModalClick,
            null, // setting personalization to its default as we dont need it for mobile
            false, // setting isMobile false
            passportBundle,
        );
        return true;
    };
    let conditionalClasses;
    const isSubscriptionChecked = subscription?.subscriptionCheckbox || false;
    if (fullWidth) {
        conditionalClasses = classes.addToCartButtonFullWidth;
    } else if (!disableAddToCart && !isSubscriptionChecked) {
        conditionalClasses = `${classes.addToCartButton} ${classes.addToCartBg}`;
    } else if (!disableAddToCart && isSubscriptionChecked) {
        conditionalClasses = `${classes.addSubscriptionToCartButton} ${classes.addToCartBg}`;
    } else {
        conditionalClasses = classes.addToCartButton;
    }
    const getAvailabilityAddCart = () => {
        let availability = false;
        if (wineValidation) {
            const keys = Object.keys(wineValidation?.wineAvailability);
            keys.forEach((key) => {
                if (wineValidation?.wineAvailability[key].isProductAvailable) {
                    availability = true;
                }
            });
        }
        return availability;
    };
    const isAddToCartDisabled = (disableAddToCart || (!isAddToCartEnabled && !getAvailabilityAddCart()));
    const errorMessage = (
        <>
            {error?.cart && error.cart !== ''
                ? (
                    <div>
                        <p className={classes.sorryMessage}>We&apos;re sorry.</p>
                        <p className={classes.outOfStockMessage}>This item is no longer available</p>
                    </div>
                )
                : null}
        </>
    );

    let layout = (
        <div className={classes.buttonWrap} id="contAddToCartBtn">
            <Button
                id="pwaAddToCartBtn"
                disabled={isAddToCartDisabled}
                onClick={() => {
                    handleAddToCart();
                    if (enableMinicart) {
                        closeAddonModal();
                    }
                }}
                className={conditionalClasses}
                variant="contained"
            >
                {isSubscriptionChecked ? 'Add Subscription to cart' : 'Add to cart'}
            </Button>
        </div>
    );
    if (productType === 'cyo') {
        const disabledCYOAddToCart = totalProductSelected === 0 || (totalProductSelected !== maxComponents) || !isBandItemSelected;
        layout = (
            <div className={classes.buttonWrap} id="contAddToCartBtn">
                <Button
                    id="pwaAddToCartBtn"
                    onClick={() => {
                        if (!getAvailabilityAddCart() && hasWine) {
                            handleMobileAgeVerification(true, handleAddToCart(null, true));
                            return;
                        }
                        handleAddToCart();
                        if (enableMinicart) {
                            closeAddonModal();
                        }
                    }}
                    className={disabledCYOAddToCart ? classes.cyoAddToCartButton : `${classes.cyoAddToCartButton} ${classes.addToCartBg}`}
                    variant="contained"
                    disabled={disabledCYOAddToCart}
                >
                    {totalProductSelected !== 0 && totalProductSelected === maxComponents && isBandItemSelected
                        ? 'Add to cart'
                        : `Select ${!isBandItemSelected ? 'Band' : `${maxComponents} Items`}`}
                </Button>
            </div>
        );
    }

    return (
        <>
            {layout}
            {errorMessage}
        </>
    );
};

DesktopFoodAddToCart.propTypes = {
    classes: object.isRequired,
    history: object.isRequired,
    error: shape({
        cart: string.isRequired,
    }),
    categoryId: string.isRequired,
    partNumber: string.isRequired,
    selectedItemId: string,
    brandId: string.isRequired,
    actionAddToCart: func.isRequired,
    subscriptionDetails: shape({
        subscriptionType: string,
        subscriptionDuration: string,
        subscriptionFrequency: string,
        subscriptionDurationKey: string,
    }),
    subscription: shape({
        subscriptionKey: array,
        subscriptionIndefinite: string,
        subscriptionType: array,
        implementProductSubscription: bool,
        viewSubscription: bool,
        isTypeDiscovery: bool,
        subscriptionCheckbox: bool,
    }),
    productType: string,
    totalProductSelected: number,
    maxComponents: number,
    fullWidth: bool,
    cyoSelectedComponents: array,
    disableAddToCart: bool,
    personalizedData: shape({
        personalizationType: string,
        personalizationLines: arrayOf({
            perAttributeValue: string,
        }),
    }),
    isPersonalizable: bool,
    wineValidation: shape({
        wineAvailability: object, // object type array
        wineVerify: shape({
            birthDay: 'string',
            birthMonth: 'string',
            birthYear: 'string',
            minAge: 'string',
        }),
    }),
    isAddToCartEnabled: bool.isRequired,
    categoryPath: string,
    categoryName: string,
    addons: arrayOf(shape({
        brandCode: string,
        productCode: number,
        componentType: string,
        productId: string,
        quantity: number,
    })),
    closeAddonModal: func,
    handleMiniCartModalClick: func,
    contactId: string,
    quantity: string,
    featureFlags: object.isRequired,
    isBandItemSelected: bool.isRequired,
    handleMobileAgeVerification: func,
    hasWine: bool,
    isPassportBundleProduct: shape({
        prices: {
            value: number,
        },
        enable: bool,
    }),
    setAddToCartClicked: func,
    selectedState: func,
};

DesktopFoodAddToCart.defaultProps = {
    selectedItemId: null,
    productType: '',
    totalProductSelected: 0,
    maxComponents: null,
    fullWidth: false,
    cyoSelectedComponents: [],
    subscriptionDetails: {},
    subscription: {},
    error: {},
    disableAddToCart: false,
    personalizedData: {},
    isPersonalizable: false,
    categoryPath: '',
    categoryName: '',
    addons: [],
    closeAddonModal: () => {},
    handleMiniCartModalClick: () => {},
    contactId: '',
    quantity: '1',
    wineValidation: null,
    handleMobileAgeVerification: () => {},
    hasWine: false,
    isPassportBundleProduct: {},
    setAddToCartClicked: () => {},
    selectedState: () => {},
};

const mapStateToProps = (state) => ({
    contactId: getContactId(state),
    zipValid: getIsProductFilterZipcodeValid(state),
    wineValidation: getWineData(state),
    showLoadingMessage: getLoaderStatus(state),
    error: getError(state),
    featureFlags: getFeatureFlags(state),
});

const mapDispatchToProps = (dispatch) => ({
    actionAddToCart: bindActionCreators(checkoutActions.addToCartFood, dispatch),
});

export default withRouter(
    connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(DesktopFoodAddToCart)),
);
